import { useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { Link } from 'react-router'
import type { OrderDetailResponse, OrderLineView, OrderProgress } from '@fitting-room/contracts'
import { CheckCircle2, ChevronDown, ChevronRight, Circle, Mail, Plus, X } from 'lucide-react'
import {
  useAdvanceOrderLine,
  useArrivalNoticePreview,
  useEmailArrivalNotice,
  useInvoiceEmailStatus,
  useRecordArrivalNotice,
  useUndoArrivalNotice,
} from '../../../lib/adminQueries'
import { BOUTIQUE_LOCALE, boutiqueDayOf } from '../../../lib/boutique'
import { shortDay } from '../../../lib/commission'
import {
  addRecipient,
  checkRecipients,
  invoiceRecipients,
  openSuggestions,
  recipientsFull,
  recipientsReady,
  sendReadiness,
  type SendReadiness,
} from '../../../lib/invoice'
import { formatCents, lineTitle } from '../../../lib/orders'
import { useStaffToken } from '../../../lib/staffToken'
import { uiStrings, type UiStrings } from '../../../lib/strings'
import { FormField, FormSheet } from '../FormSheet'
import { RecordPaymentDialog } from '../payments/RecordPaymentDialog'
import { DownloadPdfButton } from './InvoiceActions'

/**
 * Order progress (CMP-9 § 1): the order's four milestones at the top of the
 * aside — Payment, Invoice, Dresses, Pickup — each with a check, a line
 * saying where it stands, and what to do about it. What is done comes from
 * the API (`detail.progress`, `orderProgress` in `domain`); nothing here
 * works a milestone out for itself.
 *
 * The actions call the APIs the rest of the page does: Record payment opens
 * the same sheet, Mark arrived is the line's fulfillment step, Email invoice
 * the invoice page. Cancel and Restore sit at its foot; their dialogs stay
 * with the page.
 */
export function OrderProgressCard({
  detail,
  onCancel,
  onRestore,
}: {
  readonly detail: OrderDetailResponse
  readonly onCancel: () => void
  readonly onRestore: () => void
}) {
  const { token } = useStaffToken()
  const open = detail.order.status === 'open'
  const status = useInvoiceEmailStatus(token, open)
  const readiness = sendReadiness({ isError: status.isError, configured: status.data?.configured, answeredNotConfigured: false })
  return <OrderProgressView detail={detail} readiness={readiness} onCancel={onCancel} onRestore={onRestore} />
}

/** The card, given whether email can be sent. Split out so a test can say. */
export function OrderProgressView({
  detail,
  readiness,
  onCancel,
  onRestore,
}: {
  readonly detail: OrderDetailResponse
  readonly readiness: SendReadiness
  readonly onCancel: () => void
  readonly onRestore: () => void
}) {
  const t = uiStrings(BOUTIQUE_LOCALE)
  const { order, progress } = detail
  const [recording, setRecording] = useState(false)

  return (
    <section className="st-card px-4 py-3" aria-labelledby="order-progress" data-testid="order-progress">
      <h2 id="order-progress" className="st-eyebrow mb-2">
        {t.progressTitle}
      </h2>
      <ol className="space-y-3">
        <Milestone name="payment" done={progress.payment.done} title={t.progressPayment} summary={paymentSummary(t, detail)}>
          {order.status !== 'completed' ? (
            <button type="button" className="st-btn-ghost" onClick={() => setRecording(true)}>
              <Plus size={14} aria-hidden />
              {order.status === 'canceled' ? t.progressRecordRefund : t.progressRecordPayment}
            </button>
          ) : null}
        </Milestone>
        <Milestone name="invoice" done={progress.invoice.done} title={t.progressInvoice} summary={invoiceSummary(t, progress)}>
          <Link to={`/admin/orders/${encodeURIComponent(order.id)}/invoice`} className="st-btn-ghost">
            <Mail size={14} aria-hidden />
            {t.progressEmailInvoice}
          </Link>
          <DownloadPdfButton detail={detail} />
        </Milestone>
        <DressesMilestone detail={detail} readiness={readiness} />
        <Milestone name="pickup" done={progress.pickup.done} title={t.progressPickup} summary={pickupSummary(t, detail)} />
      </ol>

      {order.status === 'canceled' || order.status === 'open' ? (
        <div className="mt-3 border-t pt-2" style={{ borderColor: 'var(--st-rule)' }}>
          {order.status === 'canceled' ? (
            <button type="button" className="st-btn-quiet -ml-2" onClick={onRestore}>
              {t.progressRestoreOrder}
            </button>
          ) : (
            <button type="button" className="st-btn-quiet -ml-2" onClick={onCancel}>
              {t.progressCancelOrder}
            </button>
          )}
        </div>
      ) : null}

      <RecordPaymentDialog detail={detail} open={recording} onClose={() => setRecording(false)} />
    </section>
  )
}

/** One row: the check, the name, where it stands, and what to do. */
function Milestone({
  name,
  done,
  title,
  summary,
  children,
}: {
  readonly name: string
  readonly done: boolean
  readonly title: string
  readonly summary: ReactNode
  readonly children?: ReactNode
}) {
  return (
    <li className="flex gap-2.5" data-testid={`progress-${name}`} data-done={done ? 'true' : 'false'}>
      <StepIcon done={done} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{title}</div>
        <div className="st-meta break-words text-xs">{summary}</div>
        {children ? <div className="mt-1.5 flex flex-wrap gap-2">{children}</div> : null}
      </div>
    </li>
  )
}

function StepIcon({ done, size = 18 }: { readonly done: boolean; readonly size?: number }) {
  const t = uiStrings(BOUTIQUE_LOCALE)
  return done ? (
    <CheckCircle2 size={size} className="mt-px shrink-0" style={{ color: '#2f7d4f' }} aria-label={t.progressStepDone} role="img" />
  ) : (
    <Circle size={size} className="st-meta mt-px shrink-0" aria-label={t.progressStepNotDone} role="img" />
  )
}

/* -------------------------------- summaries -------------------------------- */

const day = (iso: string) => shortDay(boutiqueDayOf(iso))

export function paymentSummary(t: UiStrings, detail: OrderDetailResponse): string {
  const { money, balanceDue } = detail
  const paid = t.progressPaid(formatCents(money.paidMinor), formatCents(money.totalMinor))
  if (money.balanceMinor <= 0) return `${paid} · ${t.progressPaidInFull}`
  const due = balanceDue
    ? t.progressBalanceDueBy(formatCents(money.balanceMinor), shortDay(balanceDue.date))
    : t.progressBalanceDue(formatCents(money.balanceMinor))
  return `${paid} · ${due}`
}

export function invoiceSummary(t: UiStrings, progress: OrderProgress): string {
  const { lastSentAt, recipients } = progress.invoice
  if (!lastSentAt) return t.progressInvoiceNotSent
  return recipients !== undefined ? t.progressInvoiceSentTo(day(lastSentAt), recipients) : t.progressInvoiceSentOn(day(lastSentAt))
}

export function pickupSummary(t: UiStrings, detail: OrderDetailResponse): string {
  const { pickedUpAt } = detail.progress.pickup
  if (detail.progress.pickup.done && pickedUpAt) return t.progressPickedUpOn(day(pickedUpAt))
  return t.progressExpectedPickup(shortDay(detail.expectedPickup.date))
}

/* --------------------------------- dresses --------------------------------- */

type ProgressLine = OrderProgress['dresses']['lines'][number]

function DressesMilestone({ detail, readiness }: { readonly detail: OrderDetailResponse; readonly readiness: SendReadiness }) {
  const t = uiStrings(BOUTIQUE_LOCALE)
  const { dresses } = detail.progress
  // Open to start with while there is something left to do.
  const [shown, setShown] = useState(!dresses.done)
  const byId = new Map(detail.lines.map((line) => [line.id, line]))
  const rows = dresses.lines.flatMap((p) => {
    const line = byId.get(p.lineId)
    return line ? [{ p, line }] : []
  })
  const doneCount = dresses.lines.filter((line) => line.done).length
  const open = detail.order.status === 'open'
  const anyToEmail = open && rows.some(({ line }) => line.fulfillment === 'arrived' && !line.customerNotifiedAt)

  return (
    <Milestone
      name="dresses"
      done={dresses.done}
      title={t.progressDresses}
      summary={rows.length > 0 ? t.progressDressesSummary(doneCount, rows.length) : t.progressNoDresses}
    >
      {rows.length > 0 ? (
        <div className="w-full">
          <button type="button" className="st-btn-quiet -ml-2" aria-expanded={shown} onClick={() => setShown(!shown)}>
            {shown ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
            {shown ? t.progressHidePieces : t.progressShowPieces}
          </button>
          {shown ? (
            <>
              {anyToEmail ? <EmailReadinessNote t={t} readiness={readiness} /> : null}
              <ul className="mt-1.5 space-y-3">
                {rows.map(({ p, line }) => (
                  <DressProgress key={line.id} detail={detail} line={line} progress={p} readiness={readiness} />
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}
    </Milestone>
  )
}

function EmailReadinessNote({ t, readiness }: { readonly t: UiStrings; readonly readiness: SendReadiness }) {
  if (readiness === 'not_configured') {
    return (
      <p className="st-notice mt-1.5 text-xs" role="status" data-testid="progress-email-not-configured">
        {t.progressEmailNotConfigured}
      </p>
    )
  }
  if (readiness === 'unknown') {
    return (
      <p className="st-alert mt-1.5 text-xs" role="alert" data-testid="progress-email-status-failed">
        {t.progressEmailStatusFailed}
      </p>
    )
  }
  if (readiness === 'checking') {
    return (
      <p className="st-meta mt-1.5 text-xs" role="status">
        {t.progressEmailChecking}
      </p>
    )
  }
  return null
}

/** One piece: its five steps with their days, and what can be done to it now. */
function DressProgress({
  detail,
  line,
  progress,
  readiness,
}: {
  readonly detail: OrderDetailResponse
  readonly line: OrderLineView
  readonly progress: ProgressLine
  readonly readiness: SendReadiness
}) {
  const t = uiStrings(BOUTIQUE_LOCALE)
  const { token } = useStaffToken()
  const orderId = detail.order.id
  const advance = useAdvanceOrderLine(token, orderId, line.id)
  const undo = useUndoArrivalNotice(token, orderId, line.id)
  const [sheet, setSheet] = useState<'email' | 'record' | null>(null)
  const [arriving, setArriving] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const open = detail.order.status === 'open'
  const name = lineTitle(line)

  const markArrived = async () => {
    setProblem(null)
    setArriving(true)
    try {
      // The API takes one step at a time: an ordered piece is shipped on the way.
      if (line.fulfillment === 'ordered') await advance.mutateAsync({ to: 'shipped' })
      await advance.mutateAsync({ to: 'arrived' })
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error))
    } finally {
      setArriving(false)
    }
  }

  const steps: ReadonlyArray<{ key: string; label: string; at: string | undefined }> =
    progress.path === 'stock'
      ? [{ key: 'picked_up', label: t.progressStepPickedUp, at: line.pickedUpAt }]
      : [
          { key: 'ordered', label: t.progressStepOrdered, at: line.supplierOrderedAt },
          { key: 'shipped', label: t.progressStepShipped, at: line.shippedAt },
          { key: 'arrived', label: t.progressStepArrived, at: line.arrivedAt },
          { key: 'notified', label: t.progressStepNotified, at: line.customerNotifiedAt },
          { key: 'picked_up', label: t.progressStepPickedUp, at: line.pickedUpAt },
        ]

  const canArrive = open && progress.path === 'supplier' && (line.fulfillment === 'ordered' || line.fulfillment === 'shipped')
  const arrivedNotTold = open && line.fulfillment === 'arrived' && !line.customerNotifiedAt
  const toldNotGone = open && line.fulfillment === 'arrived' && Boolean(line.customerNotifiedAt)

  return (
    <li className="text-sm" data-testid="progress-piece" data-line-id={line.id} data-done={progress.done ? 'true' : 'false'}>
      <div className="flex flex-wrap items-center gap-2">
        <StepIcon done={progress.done} size={15} />
        <span className="min-w-0 break-words font-medium">{name}</span>
        {progress.path === 'stock' ? <span className="st-meta text-xs">{t.progressOffTheRack}</span> : null}
        {progress.notOrdered ? (
          <span className="st-badge st-alert-kind" data-severity="critical" data-testid="progress-not-ordered" title={t.orderNotOrderedTagHint}>
            {t.orderNotOrdered}
          </span>
        ) : null}
      </div>
      <ol className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs" aria-label={name}>
        {steps.map((step) => (
          <li key={step.key} className={step.at ? '' : 'st-meta'} data-step={step.key} data-at={step.at ? 'true' : 'false'}>
            <span className={step.at ? 'font-medium' : ''}>{step.label}</span>
            {step.at ? <span className="tabular-nums"> {day(step.at)}</span> : <span aria-hidden> —</span>}
          </li>
        ))}
      </ol>
      {line.customerNotifiedAt && line.customerNotifiedVia ? (
        <p className="st-meta mt-0.5 text-xs">{t.progressToldBy(viaWord(t, line.customerNotifiedVia))}</p>
      ) : null}

      {canArrive || arrivedNotTold || toldNotGone ? (
        <div className="mt-1.5 flex flex-wrap gap-2">
          {canArrive ? (
            <button type="button" className="st-btn-ghost" disabled={arriving} onClick={() => void markArrived()}>
              {t.progressMarkArrived}
            </button>
          ) : null}
          {arrivedNotTold ? (
            <>
              <button
                type="button"
                className="st-btn-ghost"
                disabled={readiness !== 'ready'}
                title={readiness === 'not_configured' ? t.progressEmailNotConfigured : undefined}
                onClick={() => setSheet('email')}
              >
                <Mail size={14} aria-hidden />
                {t.progressEmailCustomer}
              </button>
              <button type="button" className="st-btn-ghost" onClick={() => setSheet('record')}>
                {t.progressMarkNotified}
              </button>
            </>
          ) : null}
          {toldNotGone ? (
            <button
              type="button"
              className="st-btn-quiet"
              disabled={undo.isPending}
              onClick={() => {
                setProblem(null)
                undo.mutate(undefined, { onError: (error) => setProblem(error.message) })
              }}
            >
              {t.progressUndoNotified}
            </button>
          ) : null}
        </div>
      ) : null}
      {problem ? (
        <p className="st-alert mt-1.5 text-xs" role="alert">
          {problem}
        </p>
      ) : null}

      {sheet === 'email' ? <ArrivalEmailSheet detail={detail} line={line} onClose={() => setSheet(null)} /> : null}
      {sheet === 'record' ? <MarkNotifiedSheet detail={detail} line={line} onClose={() => setSheet(null)} /> : null}
    </li>
  )
}

function viaWord(t: UiStrings, via: NonNullable<OrderLineView['customerNotifiedVia']>): string {
  switch (via) {
    case 'email':
      return t.progressViaEmail
    case 'phone':
      return t.progressViaPhone.toLowerCase()
    case 'text':
      return t.progressViaText.toLowerCase()
    case 'in_person':
      return t.progressViaInPerson.toLowerCase()
  }
}

/* ------------------------------ the arrival email ------------------------------ */

interface Row {
  readonly id: number
  readonly value: string
}

/**
 * Who it goes to, a note, and the email as it will read — then Send. The To
 * fields start as the invoice's do (`invoiceRecipients`): the order's email,
 * with every other address on file a click away.
 */
function ArrivalEmailSheet({
  detail,
  line,
  onClose,
}: {
  readonly detail: OrderDetailResponse
  readonly line: OrderLineView
  readonly onClose: () => void
}) {
  const t = uiStrings(BOUTIQUE_LOCALE)
  const { token } = useStaffToken()
  const send = useEmailArrivalNotice(token, detail.order.id, line.id)
  const start = useMemo(() => invoiceRecipients(detail), [detail])
  const [rows, setRows] = useState<Row[]>(() => start.prefill.map((value, id) => ({ id, value })))
  const [nextId, setNextId] = useState(start.prefill.length)
  const [message, setMessage] = useState('')
  const [previewed, setPreviewed] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [notConfigured, setNotConfigured] = useState(false)
  const preview = useArrivalNoticePreview(token, detail.order.id, line.id, previewed, true)
  const caughtUp = previewCaughtUp(message, previewed, preview)

  const recipients = rows.map((row) => row.value)
  const check = checkRecipients(recipients)
  const suggestions = openSuggestions(start, recipients)
  const full = recipientsFull(recipients)

  const suggest = (address: string) => {
    const { index } = addRecipient(recipients, address)
    if (index === null) return
    const existing = rows[index]
    if (existing) setRows(rows.map((row) => (row.id === existing.id ? { ...row, value: address } : row)))
    else {
      setRows([...rows, { id: nextId, value: address }])
      setNextId(nextId + 1)
    }
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setSubmitted(true)
    if (!recipientsReady(check) || !caughtUp) return
    send.mutate(
      { to: check.to, ...(message.trim() ? { message: message.trim() } : {}) },
      {
        onSuccess: (result) => {
          if (result.sent) onClose()
          else setNotConfigured(true)
        },
      },
    )
  }

  return (
    <FormSheet open onOpenChange={(next) => (next ? undefined : onClose())} title={t.arrivalSheetTitle} description={t.arrivalSheetDescription(lineTitle(line))}>
      <form onSubmit={submit} noValidate aria-label={t.arrivalSheetTitle}>
        <fieldset className="mb-3">
          <legend className="st-eyebrow mb-1.5">{t.arrivalTo}</legend>
          <div className="space-y-1.5">
            {rows.map((row, index) => (
              <div key={row.id}>
                <div className="flex items-center gap-1.5">
                  <input
                    type="email"
                    className="st-input min-w-0 flex-1"
                    aria-label={`${t.arrivalTo} ${index + 1}`}
                    value={row.value}
                    onChange={(event) => setRows(rows.map((r) => (r.id === row.id ? { ...r, value: event.target.value } : r)))}
                  />
                  <button
                    type="button"
                    className="st-iconbtn shrink-0"
                    aria-label={t.arrivalRemoveAddress}
                    onClick={() => setRows(rows.filter((r) => r.id !== row.id))}
                  >
                    <X size={14} aria-hidden />
                  </button>
                </div>
                {submitted && check.fields[index] ? (
                  <p className="text-xs" style={{ color: '#8d3f2c' }} role="alert">
                    {check.fields[index]}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
          {suggestions.length > 0 && !full ? (
            <div className="mt-2" data-testid="arrival-suggestions">
              <p className="st-meta mb-1 text-xs">{t.arrivalOnFile}</p>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((address) => (
                  <button key={address} type="button" className="st-chip min-w-0 max-w-full" onClick={() => suggest(address)}>
                    <Plus size={12} aria-hidden />
                    <span className="truncate">{address}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {!full ? (
            <button
              type="button"
              className="st-btn-quiet -ml-2 mt-1.5"
              onClick={() => {
                setRows([...rows, { id: nextId, value: '' }])
                setNextId(nextId + 1)
              }}
            >
              <Plus size={14} aria-hidden />
              {t.arrivalAddAddress}
            </button>
          ) : null}
          {submitted && check.problem ? (
            <p className="text-xs" style={{ color: '#8d3f2c' }} role="alert">
              {check.problem}
            </p>
          ) : null}
        </fieldset>

        <FormField label={t.arrivalNote} hint={t.arrivalNoteHint}>
          <textarea
            className="st-input"
            rows={3}
            maxLength={2000}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            onBlur={() => setPreviewed(message.trim())}
          />
        </FormField>

        <div className="mb-3">
          <p className="st-eyebrow mb-1.5">{t.arrivalPreview}</p>
          {preview.data ? (
            <div className="st-card px-3 py-2 text-sm" data-testid="arrival-preview">
              <p className="mb-1.5 font-medium">{preview.data.subject}</p>
              <p className="whitespace-pre-wrap break-words">{preview.data.text}</p>
            </div>
          ) : preview.isError ? (
            <p className="st-meta text-xs" role="status">
              {t.arrivalPreviewFailed}
            </p>
          ) : (
            <p className="st-meta text-xs" role="status">
              {t.arrivalPreviewLoading}
            </p>
          )}
        </div>

        {notConfigured ? (
          <p className="st-notice mb-3 text-sm" role="status">
            {t.arrivalNotSent}
          </p>
        ) : null}
        {send.isError ? (
          <p className="st-alert mb-3 text-sm" role="alert" data-testid="arrival-send-error">
            {send.error.message}
          </p>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className="st-btn-ghost" onClick={onClose}>
            {t.progressCancel}
          </button>
          <button type="submit" className="st-btn" disabled={send.isPending || notConfigured || !caughtUp}>
            {send.isPending ? t.arrivalSending : t.arrivalSend}
          </button>
        </div>
      </form>
    </FormSheet>
  )
}

/**
 * Whether the preview on screen is of the note as it now reads, so Send can
 * go. Typing in the note and clicking Send blurs the note first: the preview
 * refetches, Send waits for it, and the stylist sends what they saw. A preview
 * that failed to load doesn't hold Send — the sheet says it's the same email.
 */
export function previewCaughtUp(
  message: string,
  previewed: string,
  preview: { readonly isPending: boolean; readonly isFetching: boolean },
): boolean {
  return previewed === message.trim() && !preview.isPending && !preview.isFetching
}

/* ------------------------------ marked by hand ------------------------------ */

const HAND_VIAS = ['phone', 'text', 'in_person'] as const
type HandVia = (typeof HAND_VIAS)[number]

function MarkNotifiedSheet({
  detail,
  line,
  onClose,
}: {
  readonly detail: OrderDetailResponse
  readonly line: OrderLineView
  readonly onClose: () => void
}) {
  const t = uiStrings(BOUTIQUE_LOCALE)
  const { token } = useStaffToken()
  const record = useRecordArrivalNotice(token, detail.order.id, line.id)
  return (
    <FormSheet open onOpenChange={(next) => (next ? undefined : onClose())} title={t.progressMarkNotified} description={t.markNotifiedDescription(lineTitle(line))}>
      <MarkNotifiedForm
        pending={record.isPending}
        error={record.error?.message}
        onCancel={onClose}
        onSave={(via) => record.mutate({ via }, { onSuccess: onClose })}
      />
    </FormSheet>
  )
}

/** How they were told — one of the three, never a default — then save. */
export function MarkNotifiedForm({
  pending,
  error,
  onCancel,
  onSave,
}: {
  readonly pending: boolean
  readonly error?: string | undefined
  readonly onCancel: () => void
  readonly onSave: (via: HandVia) => void
}) {
  const t = uiStrings(BOUTIQUE_LOCALE)
  const [via, setVia] = useState<HandVia | null>(null)
  const label: Record<HandVia, string> = { phone: t.progressViaPhone, text: t.progressViaText, in_person: t.progressViaInPerson }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (via) onSave(via)
  }

  return (
    <form onSubmit={submit} noValidate aria-label={t.progressMarkNotified} data-testid="mark-notified-form">
      <fieldset className="mb-3">
        <legend className="st-eyebrow mb-1.5">{t.markNotifiedHow}</legend>
        <div className="flex flex-wrap gap-3">
          {HAND_VIAS.map((option) => (
            <label key={option} className="flex items-center gap-1.5 text-sm">
              <input type="radio" name="via" value={option} checked={via === option} onChange={() => setVia(option)} required />
              {label[option]}
            </label>
          ))}
        </div>
      </fieldset>
      {error ? (
        <p className="st-alert mb-3 text-sm" role="alert">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className="st-btn-ghost" onClick={onCancel}>
          {t.progressCancel}
        </button>
        <button type="submit" className="st-btn" disabled={pending || via === null}>
          {pending ? t.markNotifiedSaving : t.progressMarkNotified}
        </button>
      </div>
    </form>
  )
}
